import { Badge, Button, HStack, Stack, Table, Text } from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"

import { PageShell } from "@/components/Layout"
import {
  useApplyTopicMergeProposal,
  useDismissTopicMergeProposal,
  useTopicMergeProposals,
} from "@/hooks/useTopicMergeProposals"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

export function TopicMergeProposalsPage() {
  const { data, isLoading } = useTopicMergeProposals("proposed")
  const apply = useApplyTopicMergeProposal()
  const dismiss = useDismissTopicMergeProposal()
  const breadcrumbs = useRouteBreadcrumbs("admin-topic-merge.index")

  if (isLoading) return <Text>Loading…</Text>
  const items = data?.data ?? []

  const onApply = (id: string, source: string, destination: string) => {
    // Merging is destructive: the source topic is deleted and its questions
    // move to the destination. Confirm before applying.
    const ok = window.confirm(
      `Merge "${source}" into "${destination}"?\n\nThis deletes "${source}" and moves its questions to "${destination}". This cannot be undone.`,
    )
    if (ok) apply.mutate(id)
  }

  return (
    <PageShell
      title="Topic Merges"
      description="Review suggested merges for near-duplicate topics before applying destructive changes."
      breadcrumbs={breadcrumbs}
    >
      <Stack gap={4}>
        <Text color="fg.muted" fontSize="sm">
          Near-duplicate topics suggested for consolidation. Applying a merge
          deletes the source topic and moves its questions to the destination.
        </Text>
        {items.length === 0 ? (
          <Text color="fg.muted">No merge proposals awaiting review.</Text>
        ) : (
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  Merge (source → destination)
                </Table.ColumnHeader>
                <Table.ColumnHeader>Similarity</Table.ColumnHeader>
                <Table.ColumnHeader>Reason</Table.ColumnHeader>
                <Table.ColumnHeader>Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((p) => {
                const source = p.source_topic_name ?? "(deleted)"
                const destination = p.destination_topic_name ?? "(deleted)"
                const missing = !p.source_topic_id || !p.destination_topic_id
                return (
                  <Table.Row key={p.id}>
                    <Table.Cell fontWeight="medium">
                      {source}{" "}
                      <Text as="span" color="fg.muted">
                        →
                      </Text>{" "}
                      {destination}
                    </Table.Cell>
                    <Table.Cell>
                      {p.score != null ? (
                        <Badge colorPalette="purple">
                          {p.score.toFixed(3)}
                        </Badge>
                      ) : (
                        <Text color="fg.muted">—</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell maxW="320px" truncate>
                      {p.reason ?? <Text color="fg.muted">—</Text>}
                    </Table.Cell>
                    <Table.Cell>
                      <HStack>
                        <Button
                          size="xs"
                          colorPalette="green"
                          disabled={missing}
                          loading={apply.isPending}
                          onClick={() => onApply(p.id, source, destination)}
                        >
                          Apply merge
                        </Button>
                        <Button
                          size="xs"
                          variant="outline"
                          loading={dismiss.isPending}
                          onClick={() => dismiss.mutate(p.id)}
                        >
                          Dismiss
                        </Button>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        )}
      </Stack>
    </PageShell>
  )
}

export const Route = createFileRoute("/_layout/admin-topic-merge/")({
  component: TopicMergeProposalsPage,
})
